import {
  MultiSelect,
  CountrySelector,
  TextInput,
  TagsInput,
  Select,
} from "@tfg/ui"
import { useTranslation } from "react-i18next"

import { LanguageSelector } from "~/components/LanguageSelector"
import {
  errorCodes,
  errorCodesEnum,
} from "~/pages/SupplierPortal/TraceabilityRequests/components/ZodCustomErrorMap"
import { COUNTRIES } from "~/utils/countries.ts"

import { FormFieldConfig } from "./TypesForm"

const isErrorCodeKey = (key: string): key is keyof errorCodes => {
  return Object.keys(errorCodesEnum).includes(key)
}

type Props = {
  fields: FormFieldConfig
  form: any
}

export const RenderField = ({ fields, form }: Props) => {
  const { t } = useTranslation("common")

  let word: keyof errorCodes | undefined
  if (form.errors[fields.name] && !isErrorCodeKey(form.errors[fields.name])) {
    word = errorCodesEnum.errorDefault
  } else if (isErrorCodeKey(form.errors[fields.name])) {
    word = form.errors[fields.name]
  }
  const translations = t(`zodErrors`, {
    returnObjects: true,
  }) as unknown as errorCodes
  switch (fields.type) {
    case "text":
      return (
        <>
          <TextInput
            key={fields.name}
            label={fields.label}
            placeholder={fields.placeholder}
            {...form.getInputProps(fields.name)}
            onInput={() => {
              form.validateField(fields.name)
            }}
            size="xs"
            fz="xs"
            classNames={fields.customClass}
            error={
              (word && translations[word]) ||
              (!form.isValid(fields.name) && form.isTouched(fields.name))
            }
          />
        </>
      )
    case "select":
      return (
        <Select
          key={fields.name}
          label={fields.label}
          placeholder={fields.placeholder}
          classNames={fields.customClass}
          data={
            fields.options?.map((data) => ({
              label: data.label,
              value: data.value,
            })) || []
          }
          {...form.getInputProps(fields.name)}
          error={!form.isValid(fields.name) && form.isTouched(fields.name)}
        />
      )
    case "multiselect":
      return (
        <MultiSelect
          key={fields.name}
          label={fields.label}
          searchable
          classNames={fields.customClass}
          data={
            fields.options?.map((data) => ({
              label: data.label,
              value: data.value.toString(),
            })) || []
          }
          onChange={(value) => {
            form.setFieldValue(fields.name, value)
          }}
          value={form.values[fields.name] || []}
          error={!form.isValid(fields.name) && form.isTouched()}
        />
      )
    case "countrySelect":
      return (
        <CountrySelector
          searchable
          countriesMapper={COUNTRIES}
          key={fields.name}
          label={fields.label}
          placeholder={fields.placeholder}
          classNames={fields.customClass}
          {...form.getInputProps(fields.name)}
          error={!form.isValid(fields.name) && form.isTouched()}
        />
      )
    case "languageSelect":
      return (
        <LanguageSelector
          key={fields.name}
          label={fields.label}
          placeholder={fields.placeholder}
          classNames={fields.customClass}
          {...form.getInputProps(fields.name)}
          error={!form.isValid(fields.name) && form.isTouched()}
        />
      )
    case "tagsInput":
      return (
        <TagsInput
          key={fields.name}
          label={fields.label}
          size="xs"
          classNames={fields.customClass}
          clearable
          {...form.getInputProps(fields.name)}
        />
      )
    default:
      return null
  }
}
